/**
 * Connected Platforms Card Component
 *
 * Displays list of active connections with sync controls.
 * Uses ConnectionSyncStatusCard for individual connection rows.
 */

import { Card, Space, Button, Typography, Empty } from 'antd'
import { SettingOutlined, ApiOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { ConnectionSyncStatusCard } from '@/components/connections/ConnectionSyncStatusCard'
import type { Connection } from '@/types/connection.types'
import type { InboundSyncConfig, OutboundSyncConfig } from '@/components/connections/ConnectionSyncStatusCard'

const { Text } = Typography

export interface ConnectedPlatformsCardProps {
  /** Active connections */
  connections: Connection[]

  /** Loading state */
  loading?: boolean

  /** Inbound configs by connection uid */
  inboundConfigs?: Record<string, InboundSyncConfig>

  /** Outbound configs by connection uid */
  outboundConfigs?: Record<string, OutboundSyncConfig>

  /** Callback when "Sync Now" is clicked for a connection */
  onSyncNow?: (connectionUid: string) => void

  /** Callback when auto-activate toggle is changed */
  onToggleAutoActivate?: (connectionUid: string, value: boolean) => void
}

export function ConnectedPlatformsCard({
  connections,
  loading = false,
  inboundConfigs = {},
  outboundConfigs = {},
  onSyncNow,
  onToggleAutoActivate,
}: ConnectedPlatformsCardProps) {
  const navigate = useNavigate()

  const handleManageConnections = () => {
    navigate('/connections')
  }

  return (
    <Card
      title="Connected Platforms"
      extra={
        <Button
          type="default"
          icon={<SettingOutlined />}
          onClick={handleManageConnections}
          size="small"
        >
          Manage Connections
        </Button>
      }
      loading={loading}
    >
      {connections.length === 0 ? (
        <Empty
          image={<ApiOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />}
          description={
            <Space direction="vertical" size="small">
              <Text type="secondary">No connections configured</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Connect to Square, QuickBooks, or Shopify to automatically sync your product data
              </Text>
            </Space>
          }
        >
          <Button type="primary" icon={<SettingOutlined />} onClick={handleManageConnections}>
            Manage Connections
          </Button>
        </Empty>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {connections.map((connection) => (
            <ConnectionSyncStatusCard
              key={connection.uid}
              connection={connection}
              inbound={inboundConfigs[connection.uid]}
              outbound={outboundConfigs[connection.uid]}
              onSyncNow={() => onSyncNow?.(connection.uid)}
              onToggleAutoActivate={(value) => onToggleAutoActivate?.(connection.uid, value)}
            />
          ))}
        </Space>
      )}
    </Card>
  )
}
